import { MIN_COL_RATE, QUOTE_SERVER_URL } from '@/constants';
import { getHealthFactor } from '@/utils/getHealthFactor';
import { getLogger } from '@/utils/loglevel';
import { CONST, OracleAPI, PriceQuote, VaultWallet } from '@ducat-unit/client-sdk';
import { TX } from '@ducat-unit/client-sdk/util';
import VaultAPI, {
  VaultBorrowConfig,
  VaultDepositConfig,
  VaultFeeOptions,
  VaultOpenConfig,
  VaultRepayConfig,
  VaultRepoConfig,
  VaultWithdrawConfig,
} from '@ducat-unit/client-sdk/vault';

export const VIN_ALLOWANCE = 350;
export const TX_TIMEOUT = 30_000;
const logger = getLogger('utils', 'debug');

export function computeHealthFactor_borrow({
  btcInVault,
  bitcoinPrice,
  unitInVault,
  unitAmountBorrow,
}: {
  btcInVault: number;
  bitcoinPrice: number;
  unitInVault: number;
  unitAmountBorrow: number;
}) {
  const before = parseFloat(
    (((btcInVault * bitcoinPrice) / unitInVault) * 100).toFixed(0)
  );
  const after = parseFloat(
    (
      ((btcInVault * bitcoinPrice) / (unitInVault + unitAmountBorrow)) *
      100
    ).toFixed(0)
  );

  return {
    healthFactorBefore: isFinite(before) ? before : 1,
    healthFactorAfter: isFinite(after) ? after : 1,
    healthStatusBefore: getHealthFactor(before),
    healthStatusAfter: getHealthFactor(after),
  };
}

export function computeHealthFactor_deposit({
  btcInVault,
  bitcoinPrice,
  unitInVault,
  amountBtc,
}: {
  btcInVault: number;
  bitcoinPrice: number;
  unitInVault: number;
  amountBtc: number;
}) {
  const before = parseFloat(
    (((btcInVault * bitcoinPrice) / unitInVault) * 100).toFixed(0)
  );

  const after = parseFloat(
    ((((btcInVault + amountBtc) * bitcoinPrice) / unitInVault) * 100).toFixed(0)
  );

  return {
    healthFactorBefore: isFinite(before) ? before : 1,
    healthFactorAfter: isFinite(after) ? after : 1,
    healthStatusBefore: getHealthFactor(before),
    healthStatusAfter: getHealthFactor(after),
  };
}

export function computeHealthFactor_repay({
  btcInVault,
  bitcoinPrice,
  unitInVault,
  unitAmountRepay,
}: {
  btcInVault: number;
  bitcoinPrice: number;
  unitInVault: number;
  unitAmountRepay: number;
}) {
  const before = parseFloat(
    (((btcInVault * bitcoinPrice) / unitInVault) * 100).toFixed(0)
  );

  const after = parseFloat(
    (
      ((btcInVault * bitcoinPrice) / (unitInVault - unitAmountRepay)) *
      100
    ).toFixed(0)
  );

  return {
    healthFactorBefore: isFinite(before) ? before : 1,
    healthFactorAfter: isFinite(after) ? after : 1,
    healthStatusBefore: getHealthFactor(before),
    healthStatusAfter: getHealthFactor(after),
  };
}

export function computeHealthFactor_withdraw({
  btcInVault,
  bitcoinPrice,
  unitInVault,
  amountBtc,
}: {
  btcInVault: number;
  bitcoinPrice: number;
  unitInVault: number;
  amountBtc: number;
}) {
  const before = parseFloat(
    (((btcInVault * bitcoinPrice) / unitInVault) * 100).toFixed(0)
  );

  const after = parseFloat(
    ((((btcInVault - amountBtc) * bitcoinPrice) / unitInVault) * 100).toFixed(0)
  );

  return {
    healthFactorBefore: isFinite(before) ? before : 1,
    healthFactorAfter: isFinite(after) ? after : 1,
    healthStatusBefore: getHealthFactor(before),
    healthStatusAfter: getHealthFactor(after),
  };
}

export type Utxo = {
  txid: string;
  vout: number;
  value: number;
  script: string;
};

type ScriptEnum = 'p2sh' | 'p2w-pkh' | 'p2tr';

function mapScriptTypeToSize(scriptType: ScriptEnum) {
  switch (scriptType) {
    case 'p2sh':
      return CONST.TXSIZE.TXIN.P2SH;
    case 'p2w-pkh':
      return CONST.TXSIZE.WITNESS.P2WPKH;
    case 'p2tr':
      return CONST.TXSIZE.TXIN.P2TR;
    default:
      throw Error(`Unexpected script type ${scriptType}`);
  }
}

export function calculateVinAllowance(utxos: Utxo[], feeRate: number) {
  const vsize = utxos.reduce((acc, utxo) => {
    const meta = TX.parse_script_meta(utxo.script);
    // console.log(`>>> - scriptType is ${meta.type}`);

    return acc + mapScriptTypeToSize(meta.type as ScriptEnum);
  }, 0);

  console.log(`[calculateVinAllowance]: ${vsize * feeRate}`);
  return vsize * feeRate;
}

const MAX_TRY = 5;

// Note: this is a recursive function, so limit max tries.
export async function fetchSatsUtxosAndRefine(
  cost: number,
  tryNumber: number,
  params: {
    wallet: VaultWallet;
    reqCost: number;
    feeRate: number;
  },
): Promise<Utxo[]> {
  const { wallet, reqCost, feeRate } = params;
  logger.debug(`(${tryNumber}) Selecting utxos for cost: ${cost}...`);
  const utxos = await wallet.fetch.sats_utxos(cost);
  const utxosTotalSats = utxos.reduce((acc, u) => acc + u.value, 0);
  logger.debug(`utxos (total amount: ${utxosTotalSats})`, utxos);

  const estimatedVinAllowanceSats = VIN_ALLOWANCE * feeRate;
  const actualVinAllowanceSats = calculateVinAllowance(utxos, feeRate);

  if (isNaN(reqCost)) {
    return utxos;
  }

  const actualCost = reqCost - estimatedVinAllowanceSats + actualVinAllowanceSats;
  // get_vault_deposit_change
  const change = utxosTotalSats - actualCost;
  logger.log(`original_cost=${reqCost}, actualCost=${actualCost}, change=${change} (dust_limit=${CONST.DUST_LIMIT})`);
  logger.log(
    `estimatedVinAllowanceSats=${estimatedVinAllowanceSats}, actualVinAllowanceSats=${actualVinAllowanceSats}, dust_limit=${CONST.DUST_LIMIT}`,
  );

  if (tryNumber <= MAX_TRY && change < CONST.DUST_LIMIT) {
    logger.log(`actual vin allowance produces dust change => increasing cost value from ${reqCost} to ${cost + CONST.DUST_LIMIT}`);

    return fetchSatsUtxosAndRefine(cost + CONST.DUST_LIMIT, tryNumber + 1, params);
  } else {
    logger.log(`the change output value is OK: ${change} (dust is ${CONST.DUST_LIMIT})`);
    return utxos;
  }
}

export function getMaxDepositAmount(feeRate: number, utxos: Utxo[]) {
  const opCost = getOpCostDeposit(feeRate, utxos);
  const utxosTotalSats = utxos.reduce((acc, u) => acc + u.value, 0);
  return utxosTotalSats - opCost;
}
export function getMaxDepositAmountForOpen(feeRate: number, utxos: Utxo[]) {
  const opCost = getOpCostOpen(feeRate, utxos);
  const utxosTotalSats = utxos.reduce((acc, u) => acc + u.value, 0);
  return utxosTotalSats - opCost - CONST.DUST_LIMIT;
}

export function getOpCostDeposit(feeRate: number, utxos?: Utxo[]) {
  const vinAllowanceSats = utxos ? calculateVinAllowance(utxos, feeRate) : VIN_ALLOWANCE * feeRate;
  const vault_deposit_config: VaultDepositConfig = {
    deposit_amount: 0,
    sats_address: '',
    tx_feerate: feeRate,
  };
  const tx_quote = VaultAPI.deposit.get_quote(vault_deposit_config);
  return tx_quote.total_cost + vinAllowanceSats;
}
export function getOpCostWithdraw(feeRate: number) {
  const vault_withdraw_config: VaultWithdrawConfig = {
    change_amount: 0,
    sats_address: '',
    tx_feerate: feeRate,
  };
  const tx_quote = VaultAPI.withdraw.get_quote(vault_withdraw_config);
  return tx_quote.total_cost;
}
export function getOpCostBorrow(feeRate: number) {
  const vault_borrow_config: VaultBorrowConfig = {
    deposit_amount: 0,
    borrow_amount: 0,
    unit_address: '',
    sats_address: '',
    unit_postage: 10000,
    tx_feerate: feeRate,
  };
  const tx_quote = VaultAPI.borrow.get_quote(vault_borrow_config);
  return tx_quote.total_cost;
}
export function getOpCostRepay(feeRate: number) {
  const vault_repay_config: VaultRepayConfig = {
    deposit_amount: 0,
    repay_amount: 0,
    unit_address: '',
    sats_address: '',
    unit_postage: 10000,
    tx_feerate: feeRate,
  };
  const tx_quote = VaultAPI.repay.get_quote(vault_repay_config);
  return tx_quote.total_cost;
}
const token_data = {
  rev: 0,
  tag: 'some-vault-name-long',
  ver: 1,
};
export function getOpCostOpen(feeRate: number, utxos?: Utxo[]) {
  const vinAllowanceSats = utxos ? calculateVinAllowance(utxos, feeRate) : VIN_ALLOWANCE * feeRate;
  const tx_quote = VaultAPI.open.get_quote({
    deposit_amount: 0,
    unit_postage: 10000,
    token_postage: 10000,
    token_data,
    tx_feerate: feeRate,
  } as VaultOpenConfig);

  return tx_quote.total_cost + vinAllowanceSats;
}

export function getOpCostRepo(feeRate: number, vault_count: number, utxos?: Utxo[]) {
  const vinAllowanceSats = utxos ? calculateVinAllowance(utxos, feeRate) : VIN_ALLOWANCE * feeRate;
  const tx_quote = VaultAPI.repo.get_tx_quote(
    {
      deposit_amount: 0,
      tx_feerate: feeRate,
    } as VaultRepoConfig,
    vault_count,
  );

  return tx_quote.total_cost + vinAllowanceSats;
}

export function getMaxUnit(btc: number, bitcoinPrice?: number) {
  const maxBalance = typeof bitcoinPrice !== 'undefined' && btc > 0 ? Number(Number((btc * bitcoinPrice) / MIN_COL_RATE).toFixed(2)) : null;

  return maxBalance;
}

export function getMaxUnitRounded(btc: number, bitcoinPrice?: number) {
  const maxBalance = getMaxUnit(btc, bitcoinPrice);

  return maxBalance && Math.floor(maxBalance);
}

export async function fetchPriceQuote(liquidationPrice: number): Promise<PriceQuote> {
  const th = Math.floor(liquidationPrice);
  const quoteRes = await OracleAPI.quote.fetch_price_quote(QUOTE_SERVER_URL, th === 0 ? 1 : th);
  if (quoteRes.status !== 200) {
    throw Error('Error fetching price quote');
  }

  //@ts-ignore
  // const priceQuoteData: PriceQuote = quoteRes.data;
  // return formatQuoteData(priceQuoteData);

  return quoteRes.data;
}

function formatQuoteData(priceQuoteData: any): PriceQuote {
  return {
    event_origin: null,
    event_price: null,
    event_stamp: null,
    event_type: '',
    latest_origin: '',
    latest_price: priceQuoteData.curr_price,
    latest_stamp: priceQuoteData.quote_stamp,
    quote_origin: '',
    srv_network: 'mutinynet',
    srv_pubkey: priceQuoteData.oracle_pk,
    ...priceQuoteData,
  };
}

/*
Data:
{
    "curr_price": 106762,
    "curr_stamp": 1751377200,
    "quote_price": 106762,
    "quote_stamp": 1751377200,
    "stop_price": null,
    "stop_stamp": null,
    "is_expired": false,
    "oracle_pk": "0348d6773f06f5138861d60d92558d21582d19034dfaf0c2fd7b88f2471dff26cf",
    "thold_hash": "a13cc61ebbd4f90bd5ac1592f2e8790712f9e18b",
    "thold_price": 53632,
    "thold_key": null,
    "req_id": "67cb466934a639fff9286e3263128603905af0a76c294a84397721efac9bc965",
    "req_sig": "3045022100ccc8c001a16cc40387a0e5f974453ddbd92cdba4aac8549aebd78feab176c249022051a52708d06b149a5bffcc3ee612e0787431e673bfc79a43223cf05265ad7ae9"
}

New interface:
{
    event_origin: string | null;
    event_price: number | null;
    event_stamp: number | null;
    event_type: string;
    latest_origin: string;
    latest_price: number;
    latest_stamp: number;
    quote_origin: string;
    quote_price: number;
    quote_stamp: number;
}
*/
